import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Shield, Smartphone, MessageSquare, CheckCircle, AlertTriangle, Copy, Loader2, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';

export default function MFASettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupMethod, setSetupMethod] = useState(null); // null, 'totp', 'sms'
  const [qrUrl, setQrUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [phone, setPhone] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'totp', 'sms', 'disable'

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    try { const r = await api.get('/mfa/status'); setStatus(r.data); } catch (e) { /* status unavailable */ }
    setLoading(false);
  };

  const requestPassword = (action) => {
    setCurrentPassword('');
    setPendingAction(action);
    setShowPasswordPrompt(true);
  };

  const submitPasswordAndContinue = async () => {
    if (!currentPassword.trim()) { toast.error('Enter your current password'); return; }
    setShowPasswordPrompt(false);
    if (pendingAction === 'totp') await startTOTP();
    else if (pendingAction === 'sms') { setSetupMethod('sms'); }
    else if (pendingAction === 'disable') await doDisable();
  };

  const startTOTP = async () => {
    try {
      const r = await api.post('/mfa/setup/totp', { current_password: currentPassword });
      setSecret(r.data.secret);
      setQrUrl(r.data.provisioning_uri);
      setSetupMethod('totp');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Setup failed. Check your password.');
    }
  };

  const startSMS = () => { setSetupMethod('sms'); };

  const sendSMSCode = async () => {
    if (!phone.trim()) return;
    try {
      const r = await api.post('/mfa/setup/sms', { phone });
      toast.success(`Code sent to ${r.data.phone_masked}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send code');
    }
  };

  const verifySetup = async () => {
    if (!code.trim()) return;
    setVerifying(true);
    try {
      const endpoint = setupMethod === 'totp' ? '/mfa/setup/totp/verify' : '/mfa/setup/sms/verify';
      const r = await api.post(endpoint, { code, method: setupMethod });
      if (r.data.status === 'enabled' && r.data.backup_codes) {
        setBackupCodes(r.data.backup_codes);
        toast.success('MFA enabled! Save your backup codes, then you will need to sign in again.');
        loadStatus();
      } else {
        toast.error('MFA activation could not be confirmed. Please try again.');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid code');
    }
    setVerifying(false);
  };

  const doDisable = async () => {
    if (!disableCode.trim()) { toast.error('Enter your authenticator code or backup code'); return; }
    try {
      await api.post('/mfa/disable', { code: disableCode, current_password: currentPassword });
      toast.success('MFA disabled');
      setStatus({ mfa_enabled: false });
      setSetupMethod(null);
      setBackupCodes(null);
      setDisableCode('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to disable MFA');
    }
  };

  const handleDisable = () => {
    if (!disableCode.trim()) { toast.error('Enter your authenticator code or backup code first'); return; }
    requestPassword('disable');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="co-page co-scope co-settings space-y-6" data-testid="mfa-settings-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Shield className="h-6 w-6" /> Multi-Factor Authentication</h1>
        <p className="text-gray-500 mt-1">Add an extra layer of security to your account</p>
      </div>

      {/* Current Status */}
      <Card>
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className={`h-8 w-8 ${status?.mfa_enabled ? 'text-green-600' : 'text-gray-400'}`} />
            <div>
              <p className="font-semibold">{status?.mfa_enabled ? 'MFA Enabled' : 'MFA Not Enabled'}</p>
              {status?.mfa_enabled && (
                <p className="text-sm text-gray-500">
                  Method: {status.method === 'totp' ? 'Authenticator App' : 'SMS'} · {status.backup_codes_remaining} backup codes remaining
                </p>
              )}
            </div>
          </div>
          <Badge variant="custom" className={status?.mfa_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
            {status?.mfa_enabled ? 'Active' : 'Inactive'}
          </Badge>
        </CardContent>
      </Card>

      {/* Backup codes display */}
      {backupCodes && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Save Your Backup Codes</CardTitle>
            <CardDescription>These codes can be used if you lose access to your authenticator. Each code works once. Store them safely.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {backupCodes.map((c, i) => (
                <code key={i} className="bg-[#0b2130] px-2 py-1 rounded border text-center font-mono text-sm">{c}</code>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(backupCodes.join('\n')); toast.success('Copied'); }}>
              <Copy className="h-3 w-3 mr-1" /> Copy All
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Password Reauthentication Prompt */}
      {showPasswordPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <Card className="w-full max-w-sm mx-4">
            <CardHeader>
              <CardTitle className="text-lg">Confirm Your Password</CardTitle>
              <CardDescription>Enter your current password to continue</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input
                type="password" value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="Current password" autoFocus
                onKeyDown={e => e.key === 'Enter' && submitPasswordAndContinue()}
                data-testid="mfa-password-input"
              />
              <div className="flex gap-2">
                <Button onClick={submitPasswordAndContinue} className="flex-1" data-testid="mfa-password-submit">Continue</Button>
                <Button variant="outline" onClick={() => setShowPasswordPrompt(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Setup / Disable */}
      {!status?.mfa_enabled ? (
        !setupMethod ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => requestPassword('totp')} data-testid="setup-totp-btn">
              <CardContent className="p-6 text-center">
                <Smartphone className="h-10 w-10 mx-auto text-blue-600 mb-3" />
                <h3 className="font-semibold">Authenticator App</h3>
                <p className="text-sm text-gray-500 mt-1">Microsoft Authenticator, Google Authenticator, or any TOTP app</p>
                <Badge className="mt-3 bg-blue-100 text-blue-700">Recommended</Badge>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => requestPassword('sms')} data-testid="setup-sms-btn">
              <CardContent className="p-6 text-center">
                <MessageSquare className="h-10 w-10 mx-auto text-green-600 mb-3" />
                <h3 className="font-semibold">SMS Text Message</h3>
                <p className="text-sm text-gray-500 mt-1">Receive a verification code via text message</p>
              </CardContent>
            </Card>
          </div>
        ) : setupMethod === 'totp' ? (
          <Card>
            <CardHeader>
              <CardTitle>Set Up Authenticator App</CardTitle>
              <CardDescription>Scan the QR code with your authenticator app, then enter the 6-digit code</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col items-center gap-4">
                {qrUrl && <QRCodeSVG value={qrUrl} size={200} level="M" className="border rounded p-2" data-testid="totp-qr-code" />}
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Or enter this key manually:</p>
                  <code className="bg-gray-100 px-3 py-1 rounded font-mono text-sm select-all">{secret}</code>
                </div>
              </div>
              <div className="max-w-xs mx-auto space-y-2">
                <Label>Enter 6-digit code from your app</Label>
                <Input value={code} onChange={e => setCode(e.target.value)} placeholder="000000" maxLength={6} className="text-center text-lg font-mono tracking-widest" data-testid="totp-code-input" />
                <div className="flex gap-2">
                  <Button onClick={verifySetup} disabled={verifying || code.length < 6} className="flex-1" data-testid="totp-verify-btn">
                    {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />} Verify & Enable
                  </Button>
                  <Button variant="outline" onClick={() => setSetupMethod(null)}>Cancel</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Set Up SMS Verification</CardTitle>
              <CardDescription>Enter your phone number to receive verification codes via text</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 max-w-md">
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <div className="flex gap-2">
                  <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 123-4567" data-testid="sms-phone-input" />
                  <Button variant="outline" onClick={sendSMSCode}>Send Code</Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Enter 6-digit code from SMS</Label>
                <Input value={code} onChange={e => setCode(e.target.value)} placeholder="000000" maxLength={6} className="font-mono tracking-widest" data-testid="sms-code-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={verifySetup} disabled={verifying || code.length < 6} className="flex-1" data-testid="sms-verify-btn">
                  {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />} Verify & Enable
                </Button>
                <Button variant="outline" onClick={() => setSetupMethod(null)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Disable MFA</CardTitle>
            <CardDescription>Enter a code from your authenticator app or a backup code to disable MFA</CardDescription>
          </CardHeader>
          <CardContent className="max-w-md space-y-3">
            <Input value={disableCode} onChange={e => setDisableCode(e.target.value)} placeholder="Enter code" className="font-mono" data-testid="disable-mfa-input" />
            <Button variant="destructive" onClick={handleDisable} data-testid="disable-mfa-btn">Disable MFA</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
